import Loader from "../../components/Loader";
import { Container, FormGroup, Input } from "reactstrap";
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import "./promotions.scss";
import PromotionsKPIs from "./PromotionsKPIs";
import { PropertyStoreContext } from "../../contexts/PropertyStoreContext";
import { DATE_FILTER_OPTIONS, deepClone } from "../../constants/utils";
import PromotionsTable from "./PromotionsTable";
import PromotionCreatePromo from "./PromotionCreatePromo";
import BlackFridayPageStrip from "../../components/black-friday/BlackFridayPageStrip";
import {
  activatePromo,
  deletePromo,
  disablePromo,
  getPromo,
  getRatePlansByProperty,
  getRoomsByProperty,
  getDerivedPromoDetail,
  updateDerivedPromoStatus,
} from "../../api";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Message } from "rsuite";
import { DEFAULT_CALCULATION_TYPE, PROMO_OBJECT } from "./constants";
import {
  mapDerivedPromoDetailToPromo,
  extractCalculationType,
} from "../../utils/derivedRatesMapper";
import { Can, P, usePermission } from "../../rbac";
import {
  isCampaignPromo,
  isGcRateplanPromo,
  isOmnibeesExternalPromo,
} from "./utils";
import {
  PROMO_EXPIRY_SOURCES,
  PROMO_QUERY_PARAM,
} from "../../constants/promoExpiry";
import useDeepLinkedPropertySwitch from "../../hooks/useDeepLinkedPropertySwitch";
import { getPromoVoExpiryDate, isExtension } from "../../utils/promoExtension";
import {
  trackExpiringPromoCtaClicked,
  trackPromoExtended,
} from "../../utils/promoExpiryAnalytics";
import { UAL_SOURCES } from "../../constants/ualActions";
import { EMPTY_COMBO_WARNING_MESSAGE } from "../../constants/promoValidation";
import {
  logEmptyComboBlocked,
  logEmptyComboCheckFailed,
} from "../../utils/promoComboValidation";

const SHOW_MAINTENANCE_BANNER = false; // Set to true to show the maintenance banner

const Promotions = () => {
  const { selectedProperty, isPilotHotel } = useContext(PropertyStoreContext);

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // promo.view is enough to reach this page; changing a promo needs promo.manage.
  // Without it every drawer — including one opened straight from a ?promoId
  // deep link — is read-only.
  const { allowed: canManagePromos } = usePermission(P.Promotions.Manage);

  // EO-1184 deep link: the Action Center expiry card and the expiry email both
  // send the hotel here with ?promoId=…&src=…, to land straight on the promo
  // they were told was expiring.
  const deepLinkedPromoId = searchParams.get(PROMO_QUERY_PARAM.promoId);
  const deepLinkSource = searchParams.get(PROMO_QUERY_PARAM.source);
  const deepLinkedPropertyId = searchParams.get(PROMO_QUERY_PARAM.propertyId);

  // Puts the app on the promo's hotel before openEditModal reads it — asking
  // the wrong property returns empty rather than erroring, so the drawer would
  // just open blank.
  const { isOnDeepLinkedProperty } = useDeepLinkedPropertySwitch(
    deepLinkedPropertyId,
    {
      notFoundMessage:
        "This promotion belongs to a property you don't have access to",
    }
  );

  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState("LAST_7_DAYS");
  const [ratePlans, setRatePlans] = useState([]);
  const [rooms, setRooms] = useState([]);
  // Property the rate plans/rooms above were fetched for. Combos built from
  // them are tagged with it so a payload can never pair one property's id
  // with another property's rateplan ids.
  const [ratePlansPropertyId, setRatePlansPropertyId] = useState(null);

  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isMarket, setIsMarket] = useState(false); // Initialize to false, set to true only after successful API fetch
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [promo, setPromo] = useState({});
  const [calculationType, setCalculationType] = useState();
  const [isGetPromos, setIsGetPromos] = useState({});

  // What the promo currently in the drawer looked like when it was opened.
  // Compared against the saved form state to tell an extension apart from any
  // other edit — the audit trail and the promo_extended event both need that.
  const editSessionRef = useRef(null);
  // Latest promo form state, readable from callbacks that fire in the same tick
  // the drawer clears it.
  const promoRef = useRef(promo);
  useEffect(() => {
    promoRef.current = promo;
  }, [promo]);

  const rememberExpiryBaseline = useCallback((promoVo) => {
    if (!editSessionRef.current) return;
    editSessionRef.current.previousExpiryDate = getPromoVoExpiryDate(promoVo);
    editSessionRef.current.promoName =
      promoVo?.rateRule?.ruleVO?.descriptionVO?.name ??
      editSessionRef.current.promoName;
  }, []);

  const handleRangeChange = (e) => {
    setRange(e.target.value);
  };

  const openEditModal = useCallback(
    (
      promo,
      isDuplicate = false,
      { entryPoint = "promotions_page", viewOnly = false } = {}
    ) => {
      setIsDuplicate(isDuplicate);
      setIsMarket(false);
      setIsEdit(false);
      // `viewOnly`: opened from ⋯ → View. Campaign and GC rate-plan promos are managed
      // elsewhere, so they are view-only however they are opened — the name included
      // (EO-1538). Mirrors PromotionRow's isViewable.
      setIsViewOnly(
        viewOnly ||
          !canManagePromos ||
          isOmnibeesExternalPromo(promo, selectedProperty) ||
          isCampaignPromo(promo) ||
          isGcRateplanPromo(promo)
      );
      // Snapshot what the promo looked like before the edit so a saved change
      // can be reported as an extension (and attributed to hotel vs AM) without
      // the drawer having to know about EO-1184.
      editSessionRef.current = {
        promoId: promo?.id ?? null,
        promoName: promo?.name ?? null,
        previousExpiryDate: null,
        entryPoint,
      };
      // Set a default calculation type while loading to prevent undefined prop warning
      setCalculationType(DEFAULT_CALCULATION_TYPE);

      // Initialize promo with default structure, but preserve the ID so title shows correctly
      const initialPromo = deepClone(PROMO_OBJECT);
      if (promo.id) {
        initialPromo.id = promo.id;
      }
      if (promo._derivedRateMeta?.promoId) {
        initialPromo._derivedRateMeta = {
          promoId: promo._derivedRateMeta.promoId,
        };
      }
      setPromo(initialPromo);

      setModal(true);
      setLoading(true);

      // Pilot hotel: use derived rates promo API
      if (isPilotHotel && promo.id) {
        getDerivedPromoDetail(promo.id, selectedProperty.propertyId).then(
          (response) => {
            const detail = response?.data;
            const mappedPromo = mapDerivedPromoDetailToPromo(detail);
            if (
              isDuplicate &&
              mappedPromo?.rateRule?.ruleVO?.descriptionVO?.name
            ) {
              mappedPromo.rateRule.ruleVO.descriptionVO.name = `Copy of ${mappedPromo.rateRule.ruleVO.descriptionVO.name}`;
              // Clear the derived rate meta so it creates a new promo
              delete mappedPromo._derivedRateMeta;
            }
            setPromo(mappedPromo);
            rememberExpiryBaseline(mappedPromo);
            // A deep link opens with only the id, so a Campaign is known only now —
            // at a pilot hotel by meta.promoType (see isCampaignPromo). Not for a
            // duplicate, which is a new promo and stays editable. No GC rate-plan check
            // here: pilot GC promos carry isGcCreated, not `source`, and stay editable
            // (PromotionRow's isEditable allows them; the mapper builds their GC pricing).
            if (!isDuplicate && isCampaignPromo(mappedPromo)) {
              setIsViewOnly(true);
            }
            setCalculationType(extractCalculationType(detail));
            setIsEdit(!isDuplicate);
            setLoading(false);
          },
          () => {
            setLoading(false);
            setModal(false);
            toast.error("Failed to load promo");
          }
        );
        return;
      }

      // Non-pilot: existing flow
      const originalPromoType = promo.promoType;
      const originalHtPromo = promo.htPromo;
      const originalStatus = promo.status;
      getPromo(promo.id, selectedProperty.propertyId).then(
        (response) => {
          if (isDuplicate) {
            const promoData = response?.data?.data?.promoCodeVO;
            if (promoData?.rateRule?.ruleVO?.descriptionVO?.name) {
              promoData.rateRule.ruleVO.descriptionVO.name = `Copy of ${promoData.rateRule.ruleVO.descriptionVO.name}`;
            }
            // remove rateRuleId and id from promoData else even with create action API will end up editing
            promoData.rateRule.ruleVO.descriptionVO.rateRuleId = null;
            promoData.rateRule.id = null;
            setPromo((previousPromo) => ({
              ...previousPromo,
              ...promoData,
              promoType: promoData?.promoType || originalPromoType,
            }));
          } else {
            setPromo((previousPromo) => ({
              ...previousPromo,
              ...response?.data?.data?.promoCodeVO,
              promoType:
                response?.data?.data?.promoCodeVO?.promoType ||
                originalPromoType,
              // Re-seeded like promoType above: the detail payload does not carry it,
              // and it is what marks an Omnibees promo — without it the read-only
              // banner could not say why the form is locked.
              htPromo:
                response?.data?.data?.promoCodeVO?.htPromo ?? originalHtPromo,
              status: originalStatus,
            }));
          }

          rememberExpiryBaseline(response?.data?.data?.promoCodeVO);
          // A deep link opens with only the id, so the type and source are known only
          // now. Not for a duplicate: that is a new promo being created from this one,
          // and it has to stay editable.
          const fetchedPromo = response?.data?.data?.promoCodeVO;
          if (
            !isDuplicate &&
            (isCampaignPromo({
              promoType: fetchedPromo?.promoType || originalPromoType,
            }) ||
              isGcRateplanPromo({
                source: fetchedPromo?.source ?? promo.source,
              }))
          ) {
            setIsViewOnly(true);
          }

          // Update with the actual calculation type from API (top-level or from promo payload)
          const data = response?.data?.data;
          const fromPayload =
            data?.promoCodeVO?.rateRule?.ruleVO?.discountVO?.appliedOnType;
          const calculationTypeValue =
            data?.calculationType ??
            (fromPayload == null ? null : Number(fromPayload));
          setCalculationType(
            calculationTypeValue == null
              ? DEFAULT_CALCULATION_TYPE
              : calculationTypeValue
          );
          setIsEdit(true);
          setLoading(false);
        },
        () => {
          setLoading(false);
          // Close the drawer if there's an error
          setModal(false);
          toast.error("Failed to load promo");
        }
      );
    },
    [selectedProperty, isPilotHotel, canManagePromos, rememberExpiryBaseline]
  );

  // ⋯ → View: the read-only view. The promo name goes through openEditModal, which
  // is read-only only where editing is not allowed (Campaign, GC rate-plan, Omnibees,
  // no permission).
  const openViewModal = useCallback(
    (promo) => openEditModal(promo, false, { viewOnly: true }),
    [openEditModal]
  );

  // Deep link: /promotions?promoId=123&src=expiry_card opens that promo's
  // drawer. Fires once per id so closing the drawer (which strips the params)
  // doesn't reopen it, and re-arms if a different id arrives.
  //
  // The arrival is also where expiring_promo_cta_clicked is reported, for a
  // recognised `src` only — an unknown value means the link did not come from
  // an expiry alert, and counting it would inflate the CTA.
  const openedDeepLinkRef = useRef(null);
  useEffect(() => {
    if (!deepLinkedPromoId || !selectedProperty?.propertyId) return;
    // Wait for the property switch. openEditModal fetches the promo as
    // (promoId, selectedProperty.propertyId) — firing before the switch lands
    // asks the previously selected hotel for a promo that is not theirs, and
    // that comes back empty rather than erroring, so the drawer opens blank.
    if (!isOnDeepLinkedProperty) return;
    if (openedDeepLinkRef.current === deepLinkedPromoId) return;
    openedDeepLinkRef.current = deepLinkedPromoId;

    const fromExpiryAlert = PROMO_EXPIRY_SOURCES.includes(deepLinkSource);
    if (fromExpiryAlert) {
      trackExpiringPromoCtaClicked({
        promo: { promoId: deepLinkedPromoId },
        source: deepLinkSource,
      });
    }

    // Row-shaped, matching what the table passes: `id` drives the detail fetch,
    // and _derivedRateMeta.promoId is what the update call is keyed on for a
    // pilot hotel — without it a deep-linked save would be mis-keyed.
    openEditModal(
      {
        id: deepLinkedPromoId,
        _derivedRateMeta: { promoId: deepLinkedPromoId },
      },
      false,
      { entryPoint: fromExpiryAlert ? deepLinkSource : "deep_link" }
    );
  }, [
    deepLinkedPromoId,
    deepLinkSource,
    isOnDeepLinkedProperty,
    selectedProperty?.propertyId,
    openEditModal,
  ]);

  // A promo save that pushed the end date out is an extension. Reported here
  // rather than in the drawer so it covers both entry points — the expiry CTA
  // and a plain edit from the table.
  const handlePromoSaved = useCallback((value) => {
    const session = editSessionRef.current;
    const nextExpiryDate = getPromoVoExpiryDate(promoRef.current);
    if (isExtension(session?.previousExpiryDate, nextExpiryDate)) {
      trackPromoExtended({
        promo: { promoId: session.promoId, promoName: session.promoName },
        previousExpiryDate: session.previousExpiryDate,
        nextExpiryDate,
        entryPoint: session.entryPoint,
      });
    }
    editSessionRef.current = null;
    setIsGetPromos(value ?? {});
  }, []);

  useEffect(() => {
    const propertyId = selectedProperty?.propertyId;
    if (!propertyId) {
      return;
    }

    // Drop the previous property's data immediately — keeping it visible
    // while the new request is in flight let the create-promo form build a
    // selection out of the old property's rate plans.
    setRatePlans([]);
    setRooms([]);
    setRatePlansPropertyId(null);
    setLoading(true);

    let cancelled = false;
    Promise.all([
      getRatePlansByProperty(propertyId),
      getRoomsByProperty(propertyId),
    ]).then(
      (response) => {
        // A response for a property that is no longer selected must never
        // land in state — responses can resolve out of order.
        if (cancelled) return;
        setRatePlans(response[0].data.data);
        setRooms(response[1].data.data);
        setRatePlansPropertyId(propertyId);
        setLoading(false);
      },
      () => {
        if (cancelled) return;
        setLoading(false);
        toast.error("Failed to load rooms and rate plans");
      }
    );

    return () => {
      cancelled = true;
    };
  }, [selectedProperty?.propertyId]);

  const onDisablePromo = (promo) => {
    setLoading(true);
    if (isPilotHotel && promo.id) {
      // Pilot: disable via derived rates API (sets INACTIVE)
      updateDerivedPromoStatus(
        promo.id,
        selectedProperty.propertyId,
        "INACTIVE",
        UAL_SOURCES.PROMOTIONS_LIST
      ).then(
        () => {
          setIsGetPromos({});
          setLoading(false);
          toast.success("Disabled promo successfully");
        },
        () => {
          setLoading(false);
          toast.error("Failed to disable promo");
        }
      );
      return;
    }
    disablePromo(promo.id, selectedProperty.propertyId).then(
      () => {
        setIsGetPromos({});
        setLoading(false);
        toast.success("Disabled promo successfully");
      },
      () => {
        setLoading(false);
        toast.error("Failed to disable promo");
      }
    );
  };

  // The activate call itself, unchanged. Reached only after the combo
  // pre-check below has passed (or failed open).
  const performActivate = (promo) => {
    if (isPilotHotel && promo.id) {
      updateDerivedPromoStatus(
        promo.id,
        selectedProperty.propertyId,
        "ACTIVE",
        UAL_SOURCES.PROMOTIONS_LIST
      ).then(
        () => {
          setIsGetPromos({});
          setLoading(false);
          toast.success("Activated promo successfully");
        },
        () => {
          setLoading(false);
          toast.error("Failed to activate promo");
        }
      );
      return;
    }
    activatePromo(promo.id || promo.promoId, selectedProperty.propertyId).then(
      () => {
        setLoading(false);
        setIsGetPromos({});
        toast.success("Activated promo successfully");
      },
      () => {
        setLoading(false);
        toast.error("Failed to activate promo");
      }
    );
  };

  const onActivatePromo = (promo) => {
    setLoading(true);
    const propertyId = selectedProperty.propertyId;

    // Enabling a promo with no roomtype × rateplan combination would activate
    // an empty offer. The list row only carries a `ratePlans` summary, so the
    // real selection is read from the promo detail. This fetch is a silent,
    // best-effort pre-check: no success toast, and a fetch failure shows no
    // toast either — it is logged to Rapid7 and the activate proceeds so a
    // transient read error never strands a legitimate enable.
    const detailPromise =
      isPilotHotel && promo.id
        ? getDerivedPromoDetail(promo.id, propertyId).then(
            (response) =>
              mapDerivedPromoDetailToPromo(response?.data)?.allowedRateRoomCodes
          )
        : getPromo(promo.id || promo.promoId, propertyId).then((response) => {
            // Non-empty of EITHER the new combo field or the legacy
            // rateRoomIdsAppliedTo counts as a valid selection, so a legacy
            // promo that only carries the old field is not falsely blocked.
            const promoCodeVO = response?.data?.data?.promoCodeVO;
            const codes = promoCodeVO?.allowedRateRoomCodes;
            if (codes?.length) return codes;
            return promoCodeVO?.rateRule?.ruleVO?.discountVO
              ?.rateRoomIdsAppliedTo;
          });

    detailPromise.then(
      (combos) => {
        if (!combos?.length) {
          setLoading(false);
          toast.warning(EMPTY_COMBO_WARNING_MESSAGE);
          logEmptyComboBlocked({
            action: "enable",
            promoId: promo.id || promo.promoId,
            propertyId,
          });
          return;
        }
        performActivate(promo);
      },
      (error) => {
        // Silent: no toast. Log to Rapid7 and fail open.
        logEmptyComboCheckFailed({
          action: "enable",
          promoId: promo.id || promo.promoId,
          propertyId,
          error,
        });
        performActivate(promo);
      }
    );
  };

  const onDeletePromo = (promo) => {
    setLoading(true);
    if (isPilotHotel && promo.id) {
      updateDerivedPromoStatus(
        promo.id,
        selectedProperty.propertyId,
        "DELETED",
        UAL_SOURCES.PROMOTIONS_LIST
      ).then(
        () => {
          setIsGetPromos({});
          setLoading(false);
          toast.success("Deleted promo successfully");
        },
        () => {
          setLoading(false);
          toast.error("Failed to delete promo");
        }
      );
      return;
    }
    deletePromo(promo.id, selectedProperty.propertyId).then(
      () => {
        setIsGetPromos({});
        setLoading(false);
        toast.success("Deleted promo successfully");
      },
      () => {
        setLoading(false);
        toast.error("Failed to delete promo");
      }
    );
  };

  useEffect(() => {
    if (location.state?.alignmentScore) {
      setPromo((previousPromo) => ({
        ...previousPromo,
        alignmentScore: location.state?.alignmentScore,
      }));
      navigate(location, { replace: true });
    }
  }, [location, navigate]);

  // The expiry modal's location.state hand-off is gone — it now navigates to
  // ?promoId=, handled by the deep-link effect above. That path is the only one,
  // so it cannot disagree with itself, and it is the one that carries the
  // permission gate and the CTA event.

  // Don't render content until property is loaded to prevent flickering
  if (!selectedProperty?.propertyId) {
    return null;
  }

  const toggle = () => {
    if (modal) {
      setPromo(null);
      setIsDuplicate(false);
    } else {
      setPromo(deepClone(PROMO_OBJECT));
      setCalculationType(DEFAULT_CALCULATION_TYPE);
    }
    setModal(!modal);
    setIsEdit(false);
    setIsMarket(false);
  };

  return (
    <Container
      id="promohome"
      data-heap-id="promohome"
      fluid
      className="promotions-container"
    >
      <Loader loading={loading} />

      {/* EO-1892: the Black Friday strip tops Promotions as well as Home. */}
      <BlackFridayPageStrip selectedProperty={selectedProperty} />

      {/* Header Row */}
      <div className="promotions-header">
        <h1 className="promotions-title">Promotions</h1>
        <Can permission={P.Promotions.Manage}>
          <button
            type="button"
            className="promotions-create-btn"
            onClick={toggle}
            data-testid="create-promo-header-btn"
          >
            <span aria-hidden="true" className="promotions-create-btn-plus">
              +
            </span>
            Create Promo
          </button>
        </Can>
      </div>

      {SHOW_MAINTENANCE_BANNER && (
        <div className="mb-4">
          <Message type="warning" bordered>
            <p className="mb-0">
              We are currently experiencing a critical issue with our ARI
              (Availability, Rates, and Inventory) systems. As a result,
              creating or editing promotions is temporarily unavailable. You may
              continue to view existing promotions, but please refrain from
              making changes until full service is restored.
            </p>
            <p className="my-0">
              Our engineering team is working urgently to resolve the issue and
              we expect service to resume within the next 4–6 hours. We
              apologize for any inconvenience and appreciate your understanding.
            </p>
          </Message>
        </div>
      )}

      {/* Performance Card */}
      <section className="promotions-performance-card">
        <div className="performance-card-header">
          <h2 className="performance-card-title">Performance</h2>
          <div className="performance-card-range">
            <span>Show for</span>
            <FormGroup className="promotions-filter-form">
              <Input
                type="select"
                name="select"
                id="promotions-filter-select"
                aria-label="Show performance for"
                value={range}
                onChange={handleRangeChange}
              >
                {DATE_FILTER_OPTIONS?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </div>
        </div>
        <PromotionsKPIs
          setLoading={setLoading}
          range={range}
          selectedProperty={selectedProperty}
          isPilotHotel={isPilotHotel}
        />
      </section>

      {/* Table Card */}
      <section className="promotions-table-card">
        <PromotionsTable
          selectedProperty={selectedProperty}
          onEdit={openEditModal}
          onView={openViewModal}
          onDisable={onDisablePromo}
          onActivate={onActivatePromo}
          onDelete={onDeletePromo}
          isGetPromosList={isGetPromos}
          isPilotHotel={isPilotHotel}
        />
      </section>

      {/* Create/Edit Promo Modal */}
      <PromotionCreatePromo
        ratePlans={ratePlans}
        rooms={rooms}
        ratePlansPropertyId={ratePlansPropertyId}
        modal={modal}
        setModal={setModal}
        promo={promo}
        setPromo={setPromo}
        isEdit={isEdit}
        isMarket={isMarket}
        setIsMarket={setIsMarket}
        setIsEdit={setIsEdit}
        setIsGetPromos={handlePromoSaved}
        setLoading={setLoading}
        selectedProperty={selectedProperty}
        calculationType={calculationType}
        setCalculationType={setCalculationType}
        property={selectedProperty}
        isDuplicate={isDuplicate}
        setIsDuplicate={setIsDuplicate}
        isViewOnly={isViewOnly}
        setIsViewOnly={setIsViewOnly}
        isPilotHotel={isPilotHotel}
      />
    </Container>
  );
};

export default Promotions;
